import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { getMyPdfs, getPdfUrlByFilename, uploadPdf } from "../../api/subjects";

export default function PdfPickerDialog({ open, onClose, onPick }) {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [pdfs, setPdfs] = useState([]);
    const [desc, setDesc] = useState("");

    async function load() {
        setLoading(true);
        try {
            const data = await getMyPdfs();
            setPdfs(Array.isArray(data) ? data : []);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (open) load();
    }, [open]);

    const sorted = useMemo(() => {
        return [...pdfs].sort((a, b) =>
            String(b.uploadedAt || "").localeCompare(String(a.uploadedAt || ""))
        );
    }, [pdfs]);

    async function onUploadFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const res = await uploadPdf(file, desc);
            const filename = res.filename;
            const url = getPdfUrlByFilename(filename);

            onPick?.({
                url,
                filename,
                originalFilename: res.originalFilename,
            });

            await load();
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    }

    function pickFromList(item) {
        const filename = item.filename;
        const url = getPdfUrlByFilename(filename);

        onPick?.({
            url,
            filename,
            originalFilename: item.originalFilename,
            description: item.description,
        });
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>PDF</DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                    >
                        <TextField
                            label="Підпис / description (опційно)"
                            value={desc}
                            onChange={(e) => setDesc(e.target.value)}
                            fullWidth
                        />

                        <Button variant="contained" component="label" disabled={uploading}>
                            {uploading ? <CircularProgress size={18} /> : "Завантажити файл"}
                            <input hidden type="file" accept="application/pdf,.pdf" onChange={onUploadFile} />
                        </Button>
                    </Stack>

                    {loading ? (
                        <Stack direction="row" spacing={2} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography color="text.secondary">Завантаження...</Typography>
                        </Stack>
                    ) : sorted.length === 0 ? (
                        <Typography color="text.secondary">Ще немає PDF</Typography>
                    ) : (
                        <Stack spacing={1.5} sx={{ maxHeight: 420, overflowY: "auto", pr: 1 }}>
                            {sorted.map((item) => (
                                <Box
                                    key={item._id || item.id || item.filename}
                                    onClick={() => pickFromList(item)}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        p: 1.5,
                                        cursor: "pointer",
                                        bgcolor: "background.paper",
                                        boxShadow: 1,
                                        transition: "transform 120ms ease, box-shadow 120ms ease",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: 3,
                                        },
                                    }}
                                >
                                    <Stack spacing={0.5}>
                                        <Typography fontWeight={700}>
                                            {item.originalFilename || item.filename}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {!!item.fileSize ? `${(Number(item.fileSize) / 1024).toFixed(0)} KB` : "PDF"}
                                        </Typography>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Закрити</Button>
            </DialogActions>
        </Dialog>
    );
}
